/**
 * /community/products/<product slug> — a product's hub (phase 1c): every published owner review
 * on it (each links to its thread), newest first, with the product's owner figures and the ways
 * to the product page, our review and the review form.
 *
 * Indexable while it carries at least one review: it aggregates them, and the product page
 * shows only its first page of notes. Without one it is `noindex, follow` — a thin page.
 */
import type { GetServerSideProps } from 'next'
import { publicConfig } from 'config'
import {
  fetchCommunityCategories,
  fetchCommunityCounts,
  fetchCommunityProduct,
} from 'lib/dealhunt/client'
import {
  communityHref,
  hubDescription,
  hubTitle,
  productCategoryTrail,
  productHubHref,
  productSlugParam,
  reviewAward,
  reviewCountLine,
  safeImageUrl,
  sgScoreText,
} from 'lib/community/model'
import { CommunityShell, communityServerProps, type CommunityPageProps } from 'lib/community/page'
import type {
  CommunityCategoryNode,
  CommunityPoints,
  CommunityProductResponse,
} from 'lib/community/types'
import Crumbs from 'components/community/Crumbs'
import ProductHubList, { ProductHubFollow } from 'components/community/ProductHub'
import { AboutGear, OwnItToo } from 'components/community/ThreadSide'
import base from 'components/community/community.module.css'
import styles from 'components/community/ProductHub.module.css'

interface ProductHubData {
  hub: CommunityProductResponse
  /** DealHunt's points table, for the "own it too" card's number; null when the counts read
   *  failed — the card then promises no number. */
  points: CommunityPoints | null
  /** DealHunt's category list, for the trail; null when it could not be read — the trail then
   *  falls back to the product's top-level category (`productCategoryTrail`). */
  categories: CommunityCategoryNode[] | null
}

const ProductHubPage: React.VFC<CommunityPageProps<ProductHubData>> = ({
  common,
  ts,
  data: { hub, points, categories },
}) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const { product, product_stats: stats } = hub
  const image = safeImageUrl(product.image_url)
  const award = points ? reviewAward(points, stats.published_count) : null
  const facts = [
    reviewCountLine(stats.published_count),
    stats.sg_score !== null ? `SoundGuys score ${sgScoreText(stats.sg_score)}/10` : null,
  ].filter(Boolean)
  return (
    <CommunityShell
      common={common}
      ts={ts}
      path={productHubHref(product.slug, trailingSlash)}
      title={hubTitle(product)}
      description={hubDescription(product, stats.published_count)}
      imageUrl={image}
      index={stats.published_count > 0}
    >
      <div className={base.page}>
        <header className={styles.head}>
          <Crumbs
            items={[
              { label: 'Home', href: common.homeLink.href },
              { label: 'Community', href: communityHref(trailingSlash) },
              ...productCategoryTrail(product, categories, trailingSlash),
              { label: product.name },
            ]}
          />
          <div className={styles.title}>
            <span className={styles.thumb}>
              {image && (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={image} alt="" />
              )}
            </span>
            <div className={styles.titleText}>
              <h1 className={styles.h1}>{hubTitle(product)}</h1>
              <p className={styles.facts}>{facts.join(' · ')}</p>
              <ProductHubFollow nodeId={product.node_id} />
            </div>
          </div>
        </header>
        <div className={base.columns}>
          <div className={base.main}>
            <ProductHubList initial={hub} now={ts} />
          </div>
          <aside className={base.side}>
            <OwnItToo
              product={product}
              points={award ? award.points : null}
              bonus={award?.bonus ?? false}
            />
            <AboutGear product={product} stats={stats} />
          </aside>
        </div>
      </div>
    </CommunityShell>
  )
}

export const getServerSideProps: GetServerSideProps<
  CommunityPageProps<ProductHubData>
> = async context => {
  const raw = typeof context.params?.slug === 'string' ? context.params.slug : ''
  // Not slug-shaped → 404 without asking DealHunt: a made-up path costs it nothing.
  const slug = productSlugParam(raw)
  if (!slug) {
    return { notFound: true }
  }
  const hub = await fetchCommunityProduct(slug)
  if (!hub || !hub.product.slug) {
    return { notFound: true }
  }
  // One address per product: DealHunt's slug for it (an alias or another spelling moves).
  if (raw !== hub.product.slug) {
    return {
      redirect: {
        destination: productHubHref(hub.product.slug, publicConfig('useTrailingSlash')),
        statusCode: 308,
      },
    }
  }
  // The points table rides on the counts read (public, shared-cached); the category list makes
  // the trail. Both optional: a failure costs the card its number or the trail its depth, not
  // the page.
  const [counts, list] = await Promise.all([
    fetchCommunityCounts([hub.product.node_id]).catch(() => null),
    fetchCommunityCategories().catch(() => null),
  ])
  return communityServerProps(context, () =>
    Promise.resolve({
      hub,
      points: counts?.points ?? null,
      categories: list?.categories ?? null,
    })
  )
}

export default ProductHubPage
